import type {
  CollaboratorInfo,
  InvitationRequest,
  ActivityLogEntry,
  CollaborationRole,
} from '../types/collaboration';

export class CollaborationAPI {
  /**
   * 共通のfetchオプションを取得
   */
  private getFetchOptions(method: string = 'GET', body?: object): RequestInit {
    const options: RequestInit = {
      method,
      credentials: 'include', // セッションCookieを含める
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return options;
  }

  /**
   * 共同作業者を招待
   */
  async inviteCollaborator(
    examinationId: string,
    invitation: InvitationRequest
  ): Promise<{ success: boolean; message: string; token: string }> {
    const response = await fetch(
      `/api/examinations/${examinationId}/invite-collaborator`,
      this.getFetchOptions('POST', {
        inviteEmail: invitation.email,
        inviteRole: invitation.role,
      })
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to invite collaborator');
    }

    return response.json();
  }

  /**
   * 招待を受諾
   */
  async acceptInvitation(token: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      '/api/collaborations/accept',
      this.getFetchOptions('POST', {
        acceptToken: token,
      })
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to accept invitation');
    }

    return response.json();
  }

  /**
   * 共同作業者一覧を取得
   */
  async getCollaborators(examinationId: string): Promise<CollaboratorInfo[]> {
    const response = await fetch(
      `/api/examinations/${examinationId}/collaborators`,
      this.getFetchOptions('GET')
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch collaborators');
    }

    const data = await response.json();
    return data.map((item: any) => ({
      userId: item.collabUserId,
      email: item.collabEmail,
      fullName: item.collabFullName,
      role: item.collabRole,
      status: item.collabStatus,
      invitedAt: item.collabInvitedAt,
      acceptedAt: item.collabAcceptedAt,
    }));
  }

  /**
   * 共同作業者を削除
   */
  async removeCollaborator(
    examinationId: string,
    userId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetch(
      `/api/examinations/${examinationId}/collaborators/${userId}`,
      this.getFetchOptions('DELETE')
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to remove collaborator');
    }

    return response.json();
  }

  /**
   * 活動ログを取得
   */
  async getActivityLog(examinationId: string): Promise<ActivityLogEntry[]> {
    const response = await fetch(
      `/api/examinations/${examinationId}/activity`,
      this.getFetchOptions('GET')
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to fetch activity log');
    }

    const data = await response.json();
    return data.map((item: any) => ({
      id: item.activityId,
      userId: item.activityUserId,
      userFullName: item.activityUserFullName,
      action: item.activityAction,
      targetType: item.activityTargetType,
      targetId: item.activityTargetId,
      createdAt: item.activityCreatedAt,
    }));
  }

  /**
   * 評価プロジェクトの共有を有効化
   */
  async enableSharing(examinationId: string): Promise<{ success: boolean }> {
    const response = await fetch(
      `/api/examinations/${examinationId}/sharing/enable`,
      this.getFetchOptions('POST')
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to enable sharing');
    }

    return response.json();
  }

  /**
   * 評価プロジェクトの共有を無効化
   */
  async disableSharing(examinationId: string): Promise<{ success: boolean }> {
    const response = await fetch(
      `/api/examinations/${examinationId}/sharing/disable`,
      this.getFetchOptions('POST')
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to disable sharing');
    }

    return response.json();
  }

  /**
   * 招待リンクを生成
   */
  async generateInviteLink(
    examinationId: string,
    role: CollaborationRole = 'viewer'
  ): Promise<{ success: boolean; token: string }> {
    const response = await fetch(
      `/api/examinations/${examinationId}/generate-invite`,
      this.getFetchOptions('POST', { inviteLinkRole: role })
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate invite link');
    }

    return response.json();
  }
}

// シングルトンインスタンス
export const collaborationAPI = new CollaborationAPI();
